// src/api/categoriesApi.ts
import {
  Category,
  CategoryHierarchy,
  CategoryHierarchyCombined,
  CategoryPayload,
} from "@nicsmith/shared_types";
import { baseApi } from "./api";

export const categoriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch all categories with optional filters
    getAllCategories: builder.query<CategoryHierarchy[], void>({
      query: () => {
        return {
          url: "/categories",
        };
      },
      providesTags: ["Category"],
      keepUnusedDataFor: 600, // 10 minutes — categories rarely change
    }),
    getCategoriesByModuleId: builder.query<Category[], string>({
      query: (id) => `/categories/module/${id}`,
    }),

    // Ferch all subcategories of a category
    getSubcategories: builder.query<Category[], string>({
      query: (id) => `/categories/${id}/subcategories`,
    }),

    getCategoryById: builder.query<Category, string>({
      query: (id) => `/categories/${id}`,
    }),

    getCategoryHierarchyById: builder.query<CategoryHierarchyCombined, string>({
      query: (id) => `/categories/hierarchy/${id}`,
    }),

    // Add a new category
    addCategory: builder.mutation<Category, CategoryPayload>({
      query: (category) => ({
        url: "/categories",
        method: "POST",
        body: category,
      }),
      invalidatesTags: ["Category"],
    }),

    // Update an existing category
    updateCategory: builder.mutation<
      Category,
      { id: string; data: CategoryPayload }
    >({
      query: ({ id, data }) => ({
        url: `/categories/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Category"],
    }),

    // Delete a category
    deleteCategory: builder.mutation<void, string>({
      query: (id) => ({
        url: `/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in components
export const {
  useGetAllCategoriesQuery,
  useGetCategoryByIdQuery,
  useGetCategoryHierarchyByIdQuery,
  useGetCategoriesByModuleIdQuery,
  useGetSubcategoriesQuery,
  useAddCategoryMutation,
  useUpdateCategoryMutation,
  useLazyGetSubcategoriesQuery,
  useDeleteCategoryMutation,
} = categoriesApi;
